import { useState } from 'react';
import * as Yup from 'yup';

interface FieldValidationHighlights {
  invalidFields: string[];
  validate: (values: unknown) => Promise<Record<string, never>>;
}

/**
 * Appointment-form validation-highlight pattern for care-plan forms: runs the schema
 * on Formik's `validate` pass and records the failing field paths for <Fields
 * invalidFields> (the red outline). Pair with `validateOnChange={invalidFields.length
 * > 0}` + `validateOnBlur={false}`. `validationSchema` still gates onSubmit, so
 * `validate` resolves empty.
 */
export const useFieldValidationHighlights = (schema: Yup.AnySchema): FieldValidationHighlights => {
  const [invalidFields, setInvalidFields] = useState<string[]>([]);

  // Skip the state update when the result is unchanged, avoiding a redundant re-render.
  const applyInvalidFields = (next: string[]) =>
    setInvalidFields((previous) =>
      previous.length === next.length && previous.every((path, index) => path === next[index]) ? previous : next,
    );

  const validate = async (values: unknown): Promise<Record<string, never>> => {
    try {
      await schema.validate(values, { abortEarly: false });
      applyInvalidFields([]);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        applyInvalidFields(err.inner.map(({ path }) => path).filter((path): path is string => !!path));
      }
    }
    return {};
  };

  return { invalidFields, validate };
};
